package com.shadowings.gero.data.repository

import com.shadowings.gero.domain.repository.DatabaseRepository
import com.shadowings.gero.presentation.model.Verb
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class SentenceOptions(
    val subjects: List<String>,
    val verbs: List<String>,
    val objects: List<String>,
    val possessors: List<String>,
    val places: List<String>,
    // ḥr-preposition complements for sentences_verbial.tsv rows only (locative
    // "on top of" and distributive "for/to each") - unrelated to `places`,
    // which is sentences_adverbial.tsv's own place-adverb column. Defaulted
    // so the many hand-built SentenceOptions(...) calls in tests keep compiling.
    val hrPlaceComplements: List<String> = emptyList(),
    val hrDistributionComplements: List<String> = emptyList(),
    // m-preposition locative complements ("in/inside") - a disjoint word list
    // from hrPlaceComplements (which is always "su/on top of" now): the TSV
    // column itself is the only source of truth for which preposition a place
    // word takes, no per-word override needed.
    val mPlaceComplements: List<String> = emptyList()
)

internal fun parseHierosFromTsv(tsv: String): List<Hiero> {
    return tsv.lines()
        .drop(1)
        .filter { it.isNotBlank() }
        .mapNotNull { line ->
            val columns = line.split("\t")
            if (columns.size >= 10) {
                Hiero(
                    gardinerCode = columns[0],
                    unicode = columns[1],
                    // italian = columns[3], SKIP COLUMN 3, WE USE ONLY THE FULL TRANSLITERATION
                    transliteration = columns[2],
                    ideogramma = columns[4],
                    determinativo = columns[5],
                    mnemonico = columns[6],
                    ideogrammaEn = columns[7],
                    determinativoEn = columns[8],
                    mnemonicoEn = columns[9],
                    ideogrammaDe = columns.getOrNull(10) ?: "",
                    determinativoDe = columns.getOrNull(11) ?: "",
                    mnemonicoDe = columns.getOrNull(12) ?: "",
                    ideogrammaEs = columns.getOrNull(13) ?: "",
                    determinativoEs = columns.getOrNull(14) ?: "",
                    mnemonicoEs = columns.getOrNull(15) ?: "",
                    ideogrammaFr = columns.getOrNull(16) ?: "",
                    determinativoFr = columns.getOrNull(17) ?: "",
                    mnemonicoFr = columns.getOrNull(18) ?: ""
                )
            } else {
                null
            }
        }
}

internal fun parseWordsFromTsv(tsv: String): List<Word> {
    return tsv.lines()
        .drop(1)
        .filter { it.isNotBlank() }
        .mapNotNull { line ->
            val columns = line.split("\t")
            if (columns.size >= 5) {
                Word(
                    id = columns[0],
                    meaning = columns[1],
                    reading = columns[2],
                    gardiner = columns[3],
                    note = columns[4],
                    alt1 = columns.getOrNull(5)?.takeIf { it.isNotBlank() },
                    alt2 = columns.getOrNull(6)?.takeIf { it.isNotBlank() },
                    articolo = columns.getOrNull(7)?.takeIf { it.isNotBlank() },
                    genitivo = columns.getOrNull(8)?.takeIf { it.isNotBlank() },
                    showInCompendio = columns.getOrNull(9)?.trim()?.lowercase() != "false",
                    isPlural = columns.getOrNull(14)?.trim()?.lowercase() == "true",
                    articoloEn = columns.getOrNull(10)?.takeIf { it.isNotBlank() },
                    genitivoEn = columns.getOrNull(11)?.takeIf { it.isNotBlank() },
                    noteEn = columns.getOrNull(12)?.takeIf { it.isNotBlank() },
                    meaningEn = columns.getOrNull(13)?.takeIf { it.isNotBlank() },
                    articoloDe = columns.getOrNull(15)?.takeIf { it.isNotBlank() },
                    genitivoDe = columns.getOrNull(16)?.takeIf { it.isNotBlank() },
                    noteDe = columns.getOrNull(17)?.takeIf { it.isNotBlank() },
                    meaningDe = columns.getOrNull(18)?.takeIf { it.isNotBlank() },
                    articoloEs = columns.getOrNull(19)?.takeIf { it.isNotBlank() },
                    genitivoEs = columns.getOrNull(20)?.takeIf { it.isNotBlank() },
                    noteEs = columns.getOrNull(21)?.takeIf { it.isNotBlank() },
                    meaningEs = columns.getOrNull(22)?.takeIf { it.isNotBlank() },
                    isFeminine = columns.getOrNull(23)?.trim()?.lowercase() == "true",
                    baseWordId = columns.getOrNull(24)?.takeIf { it.isNotBlank() },
                    articoloFr = columns.getOrNull(25)?.takeIf { it.isNotBlank() },
                    genitivoFr = columns.getOrNull(26)?.takeIf { it.isNotBlank() },
                    noteFr = columns.getOrNull(27)?.takeIf { it.isNotBlank() },
                    meaningFr = columns.getOrNull(28)?.takeIf { it.isNotBlank() },
                    isStative = columns.getOrNull(29)?.trim()?.lowercase() == "true",
                    itForcePresent = columns.getOrNull(30)?.trim()?.lowercase() == "true",
                    isHumanOrDeity = columns.getOrNull(31)?.trim()?.lowercase() == "true",
                    isAnimal = columns.getOrNull(32)?.trim()?.lowercase() == "true",
                    isWeakNounDe = columns.getOrNull(33)?.trim()?.lowercase() == "true",
                    isFeminineIt = columns.getOrNull(34)?.trim()?.lowercase() == "true",
                    isFeminineFr = columns.getOrNull(35)?.trim()?.lowercase() == "true",
                    esAnimalObjectNoA = columns.getOrNull(36)?.trim()?.lowercase() == "true",
                    adjOrderEs = columns.getOrNull(37)?.trim()?.takeIf { it.isNotBlank() },
                    adjOrderFr = columns.getOrNull(38)?.trim()?.takeIf { it.isNotBlank() },
                    apocopeIt = columns.getOrNull(39)?.trim()?.takeIf { it.isNotBlank() },
                    apocopeEs = columns.getOrNull(40)?.trim()?.takeIf { it.isNotBlank() },
                    frPostAdjectives = columns.getOrNull(41)?.split(",")?.map { it.trim() }?.filter { it.isNotBlank() }?.toSet().orEmpty(),
                    itPossessiveNoArticle = columns.getOrNull(42)?.trim()?.lowercase() == "true",
                    isPronoun = columns.getOrNull(43)?.trim()?.lowercase() == "true",
                    isDeity = columns.getOrNull(44)?.trim()?.lowercase() == "true",
                    deDirectional = columns.getOrNull(45)?.trim()?.lowercase() == "true"
                )
            } else {
                null
            }
        }
}

internal fun parseVerbsFromTsv(tsv: String): List<Verb> {
    return tsv.lines()
        .drop(1)
        .filter { it.isNotBlank() }
        .mapNotNull { line ->
            val columns = line.split("\t")
            if (columns.size >= 7) {
                Verb(
                    chiaveWords = columns[0],
                    formaBaseSenzaDet = columns[1],
                    traslitterazione = columns[2],
                    determinativo = columns[3],
                    formaImperfettiva = columns[4],
                    traslitterazioneImperfettiva = columns[5],
                    classe = columns[6],
                    formaInfinita = columns.getOrNull(7) ?: "",
                    traslitterazioneInfinita = columns.getOrNull(8) ?: ""
                )
            } else {
                null
            }
        }
}

// A blank cell (missing trailing column, or present but empty - e.g. a verb
// like "cantare" that has no word in words.tsv fit to be its direct object)
// means "no options here", not a single blank-id option - so it must parse
// to an empty list, never listOf("").
private fun parseCsvCell(columns: List<String>, index: Int): List<String> =
    columns.getOrNull(index)
        ?.split(",")
        ?.map { it.trim() }
        ?.filter { it.isNotEmpty() }
        ?: emptyList()

internal fun parseVerbSentencesFromTsv(tsv: String): List<SentenceOptions> {
    return tsv.lines()
        .drop(1)
        .filter { it.isNotBlank() }
        .mapNotNull { line ->
            val columns = line.split("\t")
            if (columns.size >= 2) {
                SentenceOptions(
                    subjects = parseCsvCell(columns, 0),
                    verbs = parseCsvCell(columns, 1),
                    objects = parseCsvCell(columns, 2),
                    possessors = parseCsvCell(columns, 3),
                    places = listOf(),
                    hrPlaceComplements = parseCsvCell(columns, 4),
                    hrDistributionComplements = parseCsvCell(columns, 5),
                    mPlaceComplements = parseCsvCell(columns, 6)
                )
            } else {
                null
            }
        }
}

// A row of adjectives.tsv: every adjective in the row goes with every noun in it ("la casa pura"
// doesn't, so the pairs are listed by hand). Singulars and plurals share a row: each lesson picks
// the number it needs. The Egyptian gender may differ from the translation's (pr is masculine,
// "la casa" feminine): the adjective follows the Egyptian one, which is part of the exercise.
// Left out on purpose: grande/piccolo on relatives (French "le grand père", "la petite fille" read
// as grandfather, granddaughter), PURO on SACERDOTE (wꜥb wꜥb), pairs that sound odd in some language.
data class AdjectivePairs(
    val nouns: List<String>,
    val adjectives: List<String>
)

internal fun parseAdjectivePairsFromTsv(tsv: String): List<AdjectivePairs> {
    return tsv.lines()
        .drop(1)
        .filter { it.isNotBlank() }
        .mapNotNull { line ->
            val columns = line.split("\t")
            val nouns = parseCsvCell(columns, 0)
            val adjectives = parseCsvCell(columns, 1)
            if (nouns.isNotEmpty() && adjectives.isNotEmpty()) AdjectivePairs(nouns, adjectives) else null
        }
}

// A row of possessives.tsv: every suffix pronoun in the row makes sense on every noun in it ("il
// mio cuore" does, "il mio deserto" doesn't). ESSO (st) is left out everywhere.
data class PossessivePairs(
    val nouns: List<String>,
    val pronouns: List<String>
)

internal fun parsePossessivePairsFromTsv(tsv: String): List<PossessivePairs> {
    return tsv.lines()
        .drop(1)
        .filter { it.isNotBlank() }
        .mapNotNull { line ->
            val columns = line.split("\t")
            val nouns = parseCsvCell(columns, 0)
            val pronouns = parseCsvCell(columns, 1)
            if (nouns.isNotEmpty() && pronouns.isNotEmpty()) PossessivePairs(nouns, pronouns) else null
        }
}

internal fun parseAdverbialSentencesFromTsv(tsv: String): List<SentenceOptions> {
    return tsv.lines()
        .filter { it.isNotBlank() }
        .mapNotNull { line ->
            val columns = line.split("\t")
            if (columns.size >= 2) {
                SentenceOptions(
                    subjects = columns[0].split(",").map { it.trim() },
                    verbs = listOf(),
                    objects = listOf(),
                    possessors = listOf(),
                    places = columns[1].split(",").map { it.trim() }
                )
            } else {
                null
            }
        }
}

class DatabaseRepositoryImpl : DatabaseRepository {

    companion object {
        // internal (not private) set: lets tests seed real/mock TSV content
        // directly, since DatabaseRepositoryImpl's own loader depends on
        // Compose Resources' Android reader, which needs an initialized
        // Android context and doesn't work from a plain unit test.
        var hieros: List<Hiero> = emptyList()
            internal set
        var words: List<Word> = emptyList()
            internal set
        var verbs: List<Verb> = emptyList()
            internal set
        var verbSentences: List<SentenceOptions> = emptyList()
            internal set
        var adverbialSentences: List<SentenceOptions> = emptyList()
            internal set
        var adjectivePairs: List<AdjectivePairs> = emptyList()
            internal set
        var possessivePairs: List<PossessivePairs> = emptyList()
            internal set
    }

    override val hieros: List<Hiero> get() = Companion.hieros
    override val words: List<Word> get() = Companion.words
    override val verbs: List<Verb> get() = Companion.verbs

    private val _isReady = MutableStateFlow(false)
    override val isReady: StateFlow<Boolean> = _isReady.asStateFlow()

    init {
        CoroutineScope(Dispatchers.Default).launch {
            val parsedHieros = parseHierosFromTsv(Res.readBytes("files/hiero.tsv").decodeToString())
            Companion.hieros = parsedHieros
            println("[TSV_LOADER] LOADED ${parsedHieros.size} HIEROS")

            val parsedWords = parseWordsFromTsv(Res.readBytes("files/words.tsv").decodeToString())
            Companion.words = parsedWords
            println("[TSV_LOADER] LOADED ${parsedWords.size} WORDS")

            val parsedVerbs = parseVerbsFromTsv(Res.readBytes("files/verbs.tsv").decodeToString())
            Companion.verbs = parsedVerbs
            println("[TSV_LOADER] LOADED ${parsedVerbs.size} VERBS")

            val parsedVerbSentences =
                parseVerbSentencesFromTsv(Res.readBytes("files/sentences_verbial.tsv").decodeToString())
            Companion.verbSentences = parsedVerbSentences
            println("[TSV_LOADER] LOADED ${parsedVerbSentences.size} VERB SENTENCES")

            val parsedAdverbialSentences =
                parseAdverbialSentencesFromTsv(Res.readBytes("files/sentences_adverbial.tsv").decodeToString())
            Companion.adverbialSentences = parsedAdverbialSentences
            println("[TSV_LOADER] LOADED ${parsedAdverbialSentences.size} ADVERBIAL SENTENCES")

            val parsedAdjectivePairs = parseAdjectivePairsFromTsv(Res.readBytes("files/adjectives.tsv").decodeToString())
            Companion.adjectivePairs = parsedAdjectivePairs
            println("[TSV_LOADER] LOADED ${parsedAdjectivePairs.size} ADJECTIVE PAIR ROWS")

            val parsedPossessivePairs = parsePossessivePairsFromTsv(Res.readBytes("files/possessives.tsv").decodeToString())
            Companion.possessivePairs = parsedPossessivePairs
            println("[TSV_LOADER] LOADED ${parsedPossessivePairs.size} POSSESSIVE PAIR ROWS")

            // Integrity check
            // Gardiner strings can carry MdCWord's per-row height/width modifier
            // suffixes (e.g. "G39h0.5", "Q3w0.5-G1"), which aren't real Gardiner
            // codes and must be stripped before comparing against hiero.tsv —
            // mirrors MdCWord's parseRowModifiers/ROW_MODIFIER_SUFFIX_REGEX.
            // Splitting only on the real structural separators ("-", ":", "*")
            // also avoids shredding the modifier's decimal point (the ".5" in
            // "h0.5") into a spurious standalone "5"/"6" token.
            val rowModifierSuffixRegex = Regex("^(.*?)(?:[hw]\\d+(?:\\.\\d+)?){1,2}$")
            fun stripRowModifierSuffix(code: String): String =
                rowModifierSuffixRegex.matchEntire(code)?.groupValues?.get(1) ?: code

            val allGardinerStrings =
                parsedWords.flatMap { listOfNotNull(it.gardiner, it.alt1, it.alt2) }
            val uniqueUsedCodes = allGardinerStrings
                .flatMap { it.split(Regex("[-:*]+")) }
                .map { stripRowModifierSuffix(it) }
                .filter { it.isNotBlank() }
                .distinct()

            val availableSymbolCodes = parsedHieros.map { it.gardinerCode }.toSet()
            val missingCodes = uniqueUsedCodes.filter { it !in availableSymbolCodes }

            if (missingCodes.isNotEmpty()) {
                println("[TSV_INTEGRITY] Maybe missing symbols in hiero.tsv: $missingCodes")
            } else {
                println("[TSV_INTEGRITY] All used symbols are present in hiero.tsv!")
            }

            // Check reading characters — validates both words.tsv's `reading`
            // column and hiero.tsv's `transliteration` column, since both hold
            // Egyptological transliteration text using the same character set.
            // '(' and ')' mark optional/reconstructed sounds (e.g. hiero.tsv's
            // "m(w)t") and 'j' is a valid alternative glide to 'i' (e.g. "jn",
            // "jm") — both already appear in hiero.tsv's real data.
            val allowedReadingChars = setOf(
                'r', 't', 'y', 'u', 'i', 'p', 'a', 'd', 's', 'f', 'g', 'h', 'k', 'x', 'b', 'n',
                'm', 'w', 'q', '-', '.', '(', ')',
                // Egyptological diacritics / consonants used in the TSV transliterations.
                'ꜣ', 'ꜥ', 'ḥ', 'ḫ', 'ẖ', 'ṯ', 'ḏ', 'š'
            )
            val readingsToCheck = parsedWords.map { it.id to it.reading } +
                    parsedHieros.map { it.gardinerCode to it.transliteration }
            val invalidReadings = readingsToCheck.filter { (_, reading) ->
                reading.any { char -> char.lowercaseChar() !in allowedReadingChars }
            }

            if (invalidReadings.isNotEmpty()) {
                val invalidDetails = invalidReadings.map { (id, reading) ->
                    val invalidChars =
                        reading.filter { it.lowercaseChar() !in allowedReadingChars }.toSet()
                    "$id (reading: '$reading', invalid: $invalidChars)"
                }
                println("[TSV_INTEGRITY] Words/symbols with invalid characters in reading: $invalidDetails")
            } else {
                println("[TSV_INTEGRITY] All words/symbols have valid characters in reading!")
            }

            // Check sentence-bank ID references — a typo'd word/verb ID in
            // sentences_verbial.tsv or sentences_adverbial.tsv is otherwise
            // silently dropped wherever it's consumed (e.g. a wordMap.containsKey
            // filter), never surfaced. This makes it visible at startup instead.
            val availableWordIds = parsedWords.map { it.id }.toSet()
            val availableVerbIds = parsedVerbs.map { it.chiaveWords }.toSet()

            val missingWordIdsInVerbSentences = parsedVerbSentences.flatMap {
                it.subjects + it.objects + it.possessors + it.hrPlaceComplements + it.hrDistributionComplements +
                    it.mPlaceComplements
            }.filter { it !in availableWordIds }.distinct()

            val missingVerbIdsInVerbSentences = parsedVerbSentences.flatMap { it.verbs }
                .filter { it !in availableVerbIds }.distinct()

            val missingWordIdsInAdverbialSentences = parsedAdverbialSentences
                .flatMap { it.subjects + it.places }
                .filter { it !in availableWordIds }.distinct()

            val missingWordIdsInAdjectivePairs = parsedAdjectivePairs
                .flatMap { it.nouns + it.adjectives }
                .filter { it !in availableWordIds }.distinct()

            val missingWordIdsInPossessivePairs = parsedPossessivePairs
                .flatMap { it.nouns + it.pronouns }
                .filter { it !in availableWordIds }.distinct()

            if (missingWordIdsInVerbSentences.isNotEmpty() ||
                missingVerbIdsInVerbSentences.isNotEmpty() ||
                missingWordIdsInAdverbialSentences.isNotEmpty() ||
                missingWordIdsInAdjectivePairs.isNotEmpty() ||
                missingWordIdsInPossessivePairs.isNotEmpty()
            ) {
                println(
                    "[TSV_INTEGRITY] Unknown IDs referenced in sentence banks - " +
                        "word IDs missing from words.tsv (sentences_verbial.tsv): $missingWordIdsInVerbSentences, " +
                        "verb IDs missing from verbs.tsv (sentences_verbial.tsv): $missingVerbIdsInVerbSentences, " +
                        "word IDs missing from words.tsv (sentences_adverbial.tsv): $missingWordIdsInAdverbialSentences, " +
                        "word IDs missing from words.tsv (adjectives.tsv): $missingWordIdsInAdjectivePairs, " +
                        "word IDs missing from words.tsv (possessives.tsv): $missingWordIdsInPossessivePairs"
                )
            } else {
                println("[TSV_INTEGRITY] All IDs referenced in sentence banks exist in words.tsv/verbs.tsv!")
            }

            _isReady.value = true
        }
    }
}
